import { ENDPOINT_NAMES } from '../../../constants';
import type { FieldingGroupRead } from '../../../rtk/rtk.generated';
import type { ProjectApi } from '../../../rtk/types';
import type { AppDispatch, RootState } from '../../types';
import { findMatchingCachedQueries, OptimisticUpdater } from '../../utils';

interface PatchFieldingGroupArgs {
  projectId: number;
  fieldingGroupId: string;
  fieldingGroup: {
    id?: string;
    projectId?: number;
    name?: string | null;
    internalName?: string | null;
    kitId?: number | null;
    surveys?: unknown[] | null;
    fieldingGroupAssignments?: unknown[] | null;
    studyDuration?: number | null;
    studyIncentive?: number | null;
    steps?: unknown[] | null;
    status?: string | null;
    instructionsIntro?: string | null;
  };
}

interface PatchFieldingGroupContext {
  dispatch: AppDispatch;
  queryFulfilled: Promise<{ data: FieldingGroupRead }>;
  getState: () => RootState;
}

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
type FieldingGroupQueryArgs = {
  projectId: number;
  highlighterId?: string;
  expand?: boolean;
};

/**
 * ### updateFieldingGroupInCache
 *
 * @description
 * Updates a specific fielding group in a draft array by merging the provided changes.
 *
 * @example
 * updateFieldingGroupInCache(draft, 'fg-123', { name: 'New Name' });
 *
 * @param draft - The draft array containing fielding groups
 * @param fieldingGroupId - The ID of the fielding group to update
 * @param changes - The changes to merge into the fielding group
 */
const updateFieldingGroupInCache = (
  draft: FieldingGroupRead[] | undefined,
  fieldingGroupId: string,
  changes: PatchFieldingGroupArgs['fieldingGroup'],
): void => {
  if (!draft) return;

  const fieldingGroup = draft.find((fg) => fg.id === fieldingGroupId);
  if (fieldingGroup) {
    // Merge the changes into the existing fielding group
    Object.assign(fieldingGroup, changes);
  }
};

/**
 * ### Patch Fielding Group Enhancer
 *
 * @description
 * Enhancer for patchProjectsByProjectIdFieldingGroupAndFieldingGroupId endpoint.
 * Optimistically updates the fielding group in the cache before the server responds.
 * If the request fails, the update is rolled back.
 * @param api - The ProjectApi instance
 * @returns Endpoint enhancer configuration
 */
const patchProjectsByProjectIdFieldingGroupAndFieldingGroupId = (api: ProjectApi) => ({
  // Disable tag invalidation since we're using optimistic updates
  // The generated RTK code has invalidatesTags: ['FieldingGroup'] which would
  // cause the GET query to refetch and override our optimistic update
  invalidatesTags: () => [],
  async onQueryStarted(args: PatchFieldingGroupArgs, context: PatchFieldingGroupContext) {
    const { projectId, fieldingGroupId, fieldingGroup } = args;
    const { dispatch, queryFulfilled, getState } = context;

    const updater = new OptimisticUpdater({ dispatch, api });

    // Optimistically update the fielding groups list cache (direct call for most common query pattern)
    updater.update({
      endpointName: ENDPOINT_NAMES.GET_PROJECTS_BY_PROJECT_ID_FIELDING_GROUP,
      args: { projectId, expand: true },
      updateFn: (draft: FieldingGroupRead[]) =>
        updateFieldingGroupInCache(draft, fieldingGroupId, fieldingGroup),
    });

    // Find all matching cached fielding group queries for this project
    // This handles queries with different highlighterId/expand parameters
    const state = getState();
    const queries = state.projectApi?.queries;
    const matchingQueries = findMatchingCachedQueries<FieldingGroupQueryArgs>({
      queries,
      endpointName: ENDPOINT_NAMES.GET_PROJECTS_BY_PROJECT_ID_FIELDING_GROUP,
      matcher: (queryArgs) => queryArgs.projectId === projectId,
    });

    // Update all matching cached queries (handles variations like different expand/highlighterId values)
    matchingQueries.forEach((queryArgs) => {
      updater.update({
        endpointName: ENDPOINT_NAMES.GET_PROJECTS_BY_PROJECT_ID_FIELDING_GROUP,
        args: queryArgs,
        updateFn: (draft: FieldingGroupRead[]) =>
          updateFieldingGroupInCache(draft, fieldingGroupId, fieldingGroup),
      });
    });

    try {
      await queryFulfilled;
    } catch {
      // Rollback all optimistic updates on failure
      updater.rollback();
    }
  },
});

export default patchProjectsByProjectIdFieldingGroupAndFieldingGroupId;
